/**
 * @file useBreakpointValue atom-hook — closed responsive value resolver (RFC §3).
 * @description Resolves a JS value (number, string, object — never a className)
 * from a breakpoint config against the live window width. This is the closed,
 * design-system replacement for `@/components/ui/utils/use-break-point-value`:
 * it returns a *resolved value*, carries no arbitrary className, and reads the
 * width through `useWindowDimensions` so it responds to web window resizing
 * (per the screen-size-not-device-type rule). Breakpoints mirror the project's
 * Tailwind screen scale (`sm/md/lg/xl/2xl`) so `md:`/`lg:` config keys resolve
 * identically to the class-based breakpoints.
 * @module components/atoms/useBreakpointValue
 */
import { useMemo } from "react";
import { useWindowDimensions } from "react-native";

/** Closed breakpoint keys — the Tailwind screen scale plus the base default. */
export type Breakpoint = "default" | "sm" | "md" | "lg" | "xl" | "2xl";

/**
 * Min-widths (px) for each breakpoint, mirroring the project's Tailwind screen
 * scale. `default` is the base (mobile-first) tier at width 0.
 */
const BREAKPOINT_MIN_WIDTH: Record<Breakpoint, number> = {
  default: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

/** Breakpoint keys ordered ascending by min-width (smallest → largest). */
const ORDERED_BREAKPOINTS = (
  Object.keys(BREAKPOINT_MIN_WIDTH) as Breakpoint[]
).sort((a, b) => BREAKPOINT_MIN_WIDTH[a] - BREAKPOINT_MIN_WIDTH[b]);

/** A responsive config: a partial map of breakpoint → value of type T. */
export type BreakpointValues<T> = Partial<Record<Breakpoint, T>>;

/**
 * Resolves the value that applies at the given width: the value of the largest
 * configured breakpoint whose min-width is `<= width`, falling back to the
 * nearest smaller configured breakpoint (mobile-first cascade).
 * @param values - Partial breakpoint → value config.
 * @param width - Current window width in px.
 * @returns The resolved value, or `undefined` when the config is empty.
 */
export const getBreakpointValue = <T>(
  values: BreakpointValues<T>,
  width: number
): T | undefined => {
  // Active breakpoints (min-width <= width) that the config defines, largest last.
  const active = ORDERED_BREAKPOINTS.filter(
    key =>
      BREAKPOINT_MIN_WIDTH[key] <= width &&
      Object.prototype.hasOwnProperty.call(values, key)
  );
  if (active.length > 0) {
    return values[active[active.length - 1]];
  }
  // No configured breakpoint at or below the current width: fall back to the
  // smallest configured value (mobile-first base).
  const fallbackKey = ORDERED_BREAKPOINTS.find(key =>
    Object.prototype.hasOwnProperty.call(values, key)
  );
  return fallbackKey === undefined ? undefined : values[fallbackKey];
};

/**
 * Resolves a responsive value from a breakpoint config against the live window
 * width. Re-resolves on resize (web) and orientation change (native).
 * @param values - Partial breakpoint → value config (e.g. `{ default: w * 0.9, md: 640, lg: 540 }`).
 * @returns The value for the current breakpoint.
 */
export function useBreakpointValue<T>(values: BreakpointValues<T>): T {
  const { width } = useWindowDimensions();
  return useMemo(() => getBreakpointValue(values, width) as T, [values, width]);
}
